<template>
  <div v-loading="loading" class="page">
    <el-alert
      class="mb-16px"
      type="info"
      show-icon
      :closable="false"
      title="二级代理分润从您的软件分润中按让渡比例划出，中台统一算清并各自出单。"
    />

    <el-row :gutter="16" class="mb-16px">
      <el-col :xs="24" :md="10">
        <el-card shadow="never" class="panel">
          <template #header>默认让渡比例</template>
          <div class="rate-row">
            <el-slider v-model="ratePercent" :min="0" :max="50" style="flex: 1" />
            <b class="rate-val">{{ ratePercent }}%</b>
          </div>
          <el-button class="submit-btn" type="primary" :loading="saving" @click="saveRate">
            保存让渡比例
          </el-button>
          <div class="hint">须点「保存」后才写入；为 0% 时不拆分，一级拿不到分润。已归集结算单不受影响。</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="14">
        <el-card shadow="never" class="panel">
          <template #header>邀请二级代理</template>
          <div class="invite-tip">
            将链接发给意向二级代理；对方打开后会自动选中「二级代理」并以您为上级。
          </div>
          <el-input :model-value="inviteUrl" readonly>
            <template #append>
              <el-button :disabled="!parentAgentId" @click="copy">复制</el-button>
            </template>
          </el-input>
          <div v-if="!parentAgentId" class="hint">未识别到当前代理身份，请重新登录后再复制邀请链接。</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="panel">
      <template #header>
        <div class="panel-head">
          <span>我的二级代理（{{ list.length }}）</span>
        </div>
      </template>
      <el-table :data="list" stripe>
        <el-table-column label="编号" prop="agentNo" width="140" />
        <el-table-column label="名称" prop="name" min-width="160" show-overflow-tooltip />
        <el-table-column label="手机" prop="contactMobile" width="130" />
        <el-table-column label="区域" prop="regionName" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.regionName || '-' }}</template>
        </el-table-column>
        <el-table-column label="让渡比例" width="130">
          <template #default="{ row }">
            {{ ((row.subRate || 0) * 100).toFixed(0) }}%
            <span class="rate-hint">（有效）</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag size="small" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" prop="createTime" min-width="170" />
      </el-table>
      <el-empty v-if="!list.length" description="暂无二级代理，可复制邀请链接发展下级" :image-size="72" />
    </el-card>
  </div>
</template>

<script setup lang="ts">
import {
  getPortalAccount,
  getPortalSubs,
  updatePortalAccount,
  buildAgentApplyInviteUrl
} from '@/api/agent/portal'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'AgentPortalSubs' })

const loading = ref(false)
const saving = ref(false)
const list = ref<any[]>([])
const ratePercent = ref(30)
/** 当前登录一级代理 ID，用于二级邀请链接 parent 参数 */
const parentAgentId = ref(localStorage.getItem('agent_portal_id') || '')

const inviteUrl = computed(() =>
  buildAgentApplyInviteUrl({ parent: parentAgentId.value || undefined })
)

const statusLabel = (status?: number) => {
  const map: Record<number, string> = { 0: '待审核', 1: '正常', 2: '已驳回', 3: '已停用' }
  return map[status ?? -1] || '-'
}
const statusType = (status?: number) => {
  const map: Record<number, string> = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' }
  return map[status ?? -1] || 'info'
}

const copy = async () => {
  if (!parentAgentId.value) {
    ElMessage.warning('无法生成邀请链接，请重新登录代理商后台')
    return
  }
  await navigator.clipboard.writeText(inviteUrl.value)
  ElMessage.success(`已复制：${inviteUrl.value}`)
}

onMounted(async () => {
  loading.value = true
  try {
    const acc = await getPortalAccount()
    // 必须展示库内真实值：勿用 || 30，否则库为 0 时滑条假显示 30%、未保存却以为已生效
    ratePercent.value = Math.round(Number(acc?.subRate ?? 0) * 100)
    if (acc?.id != null) {
      parentAgentId.value = String(acc.id)
      localStorage.setItem('agent_portal_id', String(acc.id))
    }
    list.value = (await getPortalSubs()) || []
  } finally {
    loading.value = false
  }
})

const saveRate = async () => {
  if (ratePercent.value <= 0) {
    ElMessage.warning('让渡比例为 0% 时不会拆分，一级拿不到分润')
  }
  saving.value = true
  try {
    await updatePortalAccount({ subRate: Number((ratePercent.value / 100).toFixed(4)) })
    list.value = (await getPortalSubs()) || []
    ElMessage.success(
      ratePercent.value > 0
        ? `已保存 ${ratePercent.value}%，后续流水按此拆分（已归集不受影响）`
        : '已保存 0%（不拆分）'
    )
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.rate-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.rate-val {
  width: 56px;
  color: #0e9c8d;
  text-align: right;
}
.hint {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}
.invite-tip {
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.submit-btn {
  background: #0e9c8d;
  border-color: #0e9c8d;
}
.submit-btn:hover {
  background: #0b8578;
  border-color: #0b8578;
}
.rate-hint {
  margin-left: 2px;
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
</style>
